import type { ReactNode } from 'react';
import React, { useState, Fragment } from 'react';
import isImage from 'is-image';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import type { Props as DynamicLinkProps } from 'Components/frameworks/dynamic-link/DynamicLink';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import Image from 'Components/frameworks/image/Image';
import { Img } from 'Components/frameworks/image/Img';
import type { RatioDisplay } from 'Components/frameworks/image/Image';
import { VideoWithFullScreen } from 'Components/frameworks/video-with-full-screen/VideoWithFullScreen';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { Backdrop } from 'Components/frameworks/backdrop/Backdrop';
import { themes } from './CardMedia.themes';
export type Media = {
  alt?: string;
  ratioKey?: string;
  ratioDisplay?: RatioDisplay;
  thumbnail?: string;
  thumbnailLabel?: string;
  title?: string;
  url: string;
};
type Props = {
  children?: ReactNode;
  closeLabel?: string;
  hasFullScreenMode?: boolean;
  link?: DynamicLinkProps | null | undefined;
  media?: Media;
  theme?: keyof typeof themes;
  title?: string;
};
export const CardMedia = ({
  children,
  closeLabel,
  hasFullScreenMode,
  link = {},
  media = {},
  theme = 'default',
  title = '',
}: Props) => {
  const { thVideo, thImageRatio } = themes[theme];
  const track = useTracking({
    actionGA: 'fullScreen',
    labelGA: `blockTitle_${title}`,
  });
  const [shouldDisplayFullScreen, setShouldDisplayFullScreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(0);

  // eslint-disable-next-line clubmed/no-fake-component
  const withLink = (node) => {
    return node && link && link.url ? (
      <DynamicLink {...link} className="block">
        {node}
      </DynamicLink>
    ) : (
      node
    );
  };

  return (
    <Fragment>
      {isImage(media.url || '') ? (
        withLink(
          <Fragment>
            {hasFullScreenMode && (
              <Fragment>
                <button
                  onClick={() => {
                    setShouldDisplayFullScreen(true);
                    track();
                  }}
                  className="absolute end-0 top-0 m-15 rounded-2 bg-white p-5 leading-0 text-black shadow-sm transition-bg-color duration-250 ease-in hocus:bg-black/60 hocus:text-white"
                  type="button"
                >
                  <Icon name={ICONS.EXPAND} width="1.25rem" />
                </button>
                {shouldDisplayFullScreen && (
                  <Backdrop
                    onClose={() => {
                      setShouldDisplayFullScreen(false);
                      setZoomLevel(0);
                    }}
                    hasCloseButton
                  >
                    {zoomLevel === 0 ? (
                      <Img dontChangeAnything altMsg={media.alt} url={media.url} ratioDisplay={1} />
                    ) : (
                      <div className="max-h-full overflow-scroll">
                        <Img
                          dontChangeAnything
                          altMsg={media.alt}
                          url={media.url}
                          ratioDisplay={1}
                          className="mx-auto block"
                          style={{
                            maxWidth: `${100 + zoomLevel * 100}%`,
                          }}
                        />
                      </div>
                    )}
                    <div className="absolute bottom-0 flex gap-x-10 p-20">
                      <button
                        className="animation-duration-500 relative top-0 z-3 flex size-26 items-center justify-center rounded-2 bg-white text-12 leading-0 text-black shadow transition-colors duration-250 ease-in ltr:animate-slideInRight rtl:animate-slideInLeft"
                        onClick={() => {
                          zoomLevel <= 4 ? setZoomLevel(zoomLevel + 1) : setZoomLevel(5);
                        }}
                        type="button"
                      >
                        <Icon name={ICONS.PLUS} />
                      </button>
                      <button
                        className="animation-duration-500 relative top-0 z-3 flex size-26 items-center justify-center rounded-2 bg-white text-12 leading-0 text-black shadow transition-colors duration-250 ease-in ltr:animate-slideInRight rtl:animate-slideInLeft"
                        onClick={() => {
                          zoomLevel >= 1 ? setZoomLevel(zoomLevel - 1) : setZoomLevel(0);
                        }}
                        type="button"
                      >
                        <Icon name={ICONS.MINUS} />
                      </button>
                    </div>
                  </Backdrop>
                )}
              </Fragment>
            )}

            <Image
              altMsg={media.alt}
              imageUrl={media.url}
              ratioKey={media.ratioKey || thImageRatio}
              ratioDisplay={media.ratioDisplay || 1 / 2}
            />
          </Fragment>,
        )
      ) : (
        <VideoWithFullScreen
          buttonLabel={media.thumbnailLabel}
          className={thVideo}
          closeLabel={closeLabel}
          thumbnail={media.thumbnail || ''}
          title={media.title}
          url={media.url}
        />
      )}
      {withLink(children)}
    </Fragment>
  );
};
